import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';

import { Subscription } from 'rxjs';

import { ConfigurationState, PriorityNewsItem, WeatherResponse } from '../shared/interfaces';
import { MailFeedModel } from '../shared/models/mail';
import { DsLoggingService } from '../shared/services/ds-logging.service';
import { Clock } from './clock/clock';
import { Disposable } from './clock/interfaces';
import { ConfigService } from './config/config.service';
import { RefreshTier } from './config/enums';
import { SignageConfigurationModel } from './config/models';
import { ContentRegistryService } from './content/content-registry.service';
import { ContentSnapshotModel } from './content/models';
import { SignageChangeModel } from './data/models';
import { SignageDataSource } from './data/signage-data-source';
import { RuntimeState } from './enums';
import { ScopedContentStoreService } from './persistence/scoped-content-store.service';
import { InterruptSource, PlaybackState } from './playback/enums';
import { PlaybackService } from './playback/playback.service';
import { SIGNAGE_PROFILE } from './profile';
import { NetworkMonitorService } from './resilience/network-monitor.service';
import { WatchdogService } from './resilience/watchdog.service';
import { RefreshSchedulerService } from './scheduler/refresh-scheduler.service';
import { ContentKind } from './slides/enums';
import { RuntimeSlideModel } from './slides/models';
import { SlideScheduleService } from './slides/slide-schedule.service';
import { DiagnosticVerbosity, HeartbeatState, ShutdownReason } from './telemetry/enums';
import { HeartbeatPayloadModel } from './telemetry/models';

// Telemetry has no Idle: a runtime that is not playing anything reports as empty.
const HEARTBEAT_STATES: Readonly<Record<RuntimeState, HeartbeatState>> = {
  [RuntimeState.Idle]: HeartbeatState.Empty,
  [RuntimeState.Starting]: HeartbeatState.Starting,
  [RuntimeState.Playing]: HeartbeatState.Playing,
  [RuntimeState.Offline]: HeartbeatState.Offline,
  [RuntimeState.Empty]: HeartbeatState.Empty
};
const CACHE_LIFETIME_MS = 7 * 24 * 60 * 60 * 1000;
const INTERRUPT_CHECK_MS = 30 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const INTERRUPT_RETRY_MS = 60 * 1000;
const INTERRUPT_ATTEMPTS = 2;

// The conductor: loads the channel, drives the refresh tiers into the registry, feeds slides to playback, reconciles interrupts.
@Injectable()
export class SignageRuntimeService implements OnDestroy {
  private readonly clock = inject(Clock);
  private readonly schedule = inject(SlideScheduleService);
  private readonly logging = inject(DsLoggingService);
  private readonly configService = inject(ConfigService);
  private readonly registry = inject(ContentRegistryService);
  private readonly playback = inject(PlaybackService);
  private readonly scheduler = inject(RefreshSchedulerService);
  private readonly network = inject(NetworkMonitorService);
  private readonly watchdog = inject(WatchdogService);
  private readonly source = inject(SignageDataSource);
  private readonly store = inject(ScopedContentStoreService);
  private readonly profile = inject(SIGNAGE_PROFILE);
  private readonly starting = signal(false);
  private readonly blockedUntil = new Map<string, number>();
  private readonly attempts = new Map<string, number>();
  private session?: AbortController;
  private push?: Subscription;
  private interruptTicker?: Disposable;
  private dayTicker?: Disposable;
  private supplied?: ConfigurationState;
  private lastCompletedGeneration = -1;
  private reconnect?: () => void;

  public readonly configuration = this.configService.configuration;
  public readonly current = this.playback.current;
  public readonly feedHealth = this.registry.feedHealth;
  public readonly slidesShownSinceStart = this.playback.slidesShownSinceStart;
  public readonly state = computed<RuntimeState>(() => {
    if (this.starting()) {
      return RuntimeState.Starting;
    }

    if (this.playback.state() === PlaybackState.Stopped) {
      return RuntimeState.Idle;
    }

    if (this.playback.current()) {
      return RuntimeState.Playing;
    }

    return this.network.online() ? RuntimeState.Empty : RuntimeState.Offline;
  });
  public readonly priorityMessages = computed(() => this.overlay(ContentKind.PriorityNews)
    .map(slide => slide.content.priority).filter((item): item is PriorityNewsItem => !!item));
  public readonly weather = computed<WeatherResponse | undefined>(
    () => this.overlay(ContentKind.Weather)[0]?.content.weather);
  public readonly mail = computed<MailFeedModel | undefined>(() => this.overlay(ContentKind.Mail)[0]?.content.mail);

  public async start(configuration?: ConfigurationState): Promise<void> {
    this.stop();
    this.supplied = configuration;
    const session = new AbortController();
    this.session = session;
    this.starting.set(true);
    this.logging.beginSession();

    try {
      const config = await this.configService.load(configuration, session.signal);

      if (session.signal.aborted) {
        return;
      }

      this.registry.activate(config);
      this.scheduler.begin((tier, signal) => this.refresh(tier, signal));
      // Cache before network, so a display back from a power cut shows content instead of an empty board.
      await this.restoreCache(config, session.signal);
      await this.scheduler.run(RefreshTier.Main, RefreshTier.Secondary);

      if (session.signal.aborted) {
        return;
      }

      this.playback.start(this.registry.slides());
      this.reconcileInterrupts();
      this.beginBackgroundWork(config, session);
    } finally {
      this.starting.set(false);
    }
  }

  /** Channel switching is a first-class operation, not a page reload. */
  public async setConfiguration(configuration: ConfigurationState): Promise<void> {
    this.logging.markCleanShutdown(ShutdownReason.ChannelSwitch);
    await this.start(configuration);
  }

  public advance(direction: 1 | -1 = 1): void {
    this.playback.advance(direction);
  }

  public async restart(): Promise<void> {
    this.logging.markCleanShutdown(ShutdownReason.WatchdogRestart);
    await this.start(this.supplied);
  }

  public stop(): void {
    this.session?.abort();
    this.session = undefined;
    this.push?.unsubscribe();
    this.push = undefined;
    this.interruptTicker?.dispose();
    this.dayTicker?.dispose();
    this.reconnect?.();
    this.reconnect = undefined;
    this.interruptTicker = undefined;
    this.dayTicker = undefined;
    this.scheduler.stop();
    this.watchdog.stop();
    this.playback.stop();
    this.blockedUntil.clear();
    this.attempts.clear();
  }

  public ngOnDestroy(): void {
    this.stop();
  }

  private beginBackgroundWork(config: SignageConfigurationModel, session: AbortController): void {
    this.interruptTicker = this.clock.every(INTERRUPT_CHECK_MS, 0, () => this.reconcileInterrupts());
    // Activities and meals are fetched for the community's calendar date, so the day has to roll over in its timezone.
    this.dayTicker = this.clock.every(DAY_MS, 0, () => void this.scheduler.run(RefreshTier.Main));
    this.reconnect = this.network.onReconnect(() => void this.scheduler.run(RefreshTier.Main, RefreshTier.Secondary));

    // Display-only: a preview inside ClientApp must not subscribe to PubNub or restart under the user.
    if (this.profile !== 'display') {
      return;
    }

    this.push = this.source.changes(config.channel).subscribe({
      next: change => this.applyChange(change),
      error: () => this.logging.trackEvent('push_unavailable')
    });
    session.signal.addEventListener('abort', () => this.push?.unsubscribe(), { once: true });
    this.watchdog.start(() => void this.restart());
  }

  private async refresh(tier: RefreshTier, signal: AbortSignal): Promise<void> {
    if (tier === RefreshTier.Config) {
      const reloaded = await this.configService.load(this.supplied, signal);

      if (signal.aborted) {
        return;
      }

      this.registry.activate(reloaded);
    }

    await this.registry.refresh(tier, signal);

    if (signal.aborted) {
      return;
    }

    this.playback.stage(this.registry.slides());
    this.reconcileInterrupts();
    await this.persistCache(signal);
  }

  private applyChange(change: SignageChangeModel): void {
    switch (change.kind) {
      case 'diagnostics':
        this.logging.setVerbosity(change.verbosity ?? DiagnosticVerbosity.Normal);

        return;
      case 'configuration':
        void this.scheduler.run(RefreshTier.Config);

        return;
      case 'priority-news':
        void this.scheduler.run(RefreshTier.Secondary);

        return;
      default:
        void this.scheduler.run(RefreshTier.Main);
    }
  }

  private reconcileInterrupts(): void {
    const now = this.clock.now().toEpochMilli();
    this.recordCompletedInterrupt(now);
    const available = this.registry.interrupts()
      .filter(slide => (this.blockedUntil.get(slide.id) ?? 0) <= now && this.schedule.isEligible(slide, now));
    // A live stream is whatever is on air now; scheduled videos queue by start time, earliest first.
    const live = available.find(slide => slide.kind === 'youtube-live');
    const scheduled = available.filter(slide => slide.kind !== 'youtube-live')
      .sort((first, second) => (first.meta.startsAt ?? 0) - (second.meta.startsAt ?? 0))[0];
    if (live) {
      this.playback.interrupt(live, InterruptSource.Live);
    } else {
      this.playback.resume(InterruptSource.Live);
    }

    if (scheduled) {
      this.playback.interrupt(scheduled, InterruptSource.Scheduled);
    } else {
      this.playback.resume(InterruptSource.Scheduled);
    }
  }

  private recordCompletedInterrupt(now: number): void {
    const completed = this.playback.completedInterrupt();

    if (!completed || completed.generation === this.lastCompletedGeneration) {
      return;
    }

    this.lastCompletedGeneration = completed.generation;
    const attempts = (this.attempts.get(completed.id) ?? 0) + 1;
    this.attempts.set(completed.id, attempts);
    // A video that never reached the screen gets one more chance a minute later; anything else is finished with.
    this.blockedUntil.set(completed.id,
      completed.failed && attempts < INTERRUPT_ATTEMPTS ? now + INTERRUPT_RETRY_MS : Number.POSITIVE_INFINITY);
  }

  private overlay(kind: ContentKind): readonly RuntimeSlideModel[] {
    return this.registry.overlays().filter(slide => slide.kind === kind);
  }

  private async restoreCache(config: SignageConfigurationModel, signal: AbortSignal): Promise<void> {
    const snapshot = await this.store.read<ContentSnapshotModel>(this.cacheKey(config), signal);

    if (snapshot && !signal.aborted) {
      this.registry.restore(snapshot);
    }
  }

  private async persistCache(signal: AbortSignal): Promise<void> {
    const config = this.configuration();

    if (!config) {
      return;
    }

    await this.store.write(this.cacheKey(config), this.registry.snapshot(),
      this.clock.now().toEpochMilli() + CACHE_LIFETIME_MS, signal);
  }

  private cacheKey(config: SignageConfigurationModel): string {
    return `snapshot:${config.channel.providerUuid}:${config.channel.id}`;
  }

  /** The runtime's contribution to the host's heartbeat; the host owns the schedule and the device block. */
  public diagnostics(): HeartbeatPayloadModel {
    const feeds = this.feedHealth();
    const now = this.clock.now().toEpochMilli();
    const state = this.state();

    return {
      activeTimers: this.clock.activeTimers,
      state: HEARTBEAT_STATES[state],
      playlistLength: this.playback.count(), slidesShownSinceStart: this.slidesShownSinceStart(),
      currentSlideKind: this.playback.current()?.kind ?? null,
      online: this.network.online(), offlineEventsSinceStart: this.network.offlineEventsSinceStart(),
      consecutiveFeedFailures: Object.values(feeds).reduce((total, feed) => total + feed.failures, 0),
      feeds: Object.fromEntries(Object.entries(feeds).map(([kind, feed]) => [kind, {
        lastSuccessAgoMs: feed.lastSuccess === undefined ? null : now - feed.lastSuccess,
        consecutiveFailures: feed.failures
      }]))
    };
  }
}
